// react
import styled from 'styled-components';

// components
import { Button, Card, Container, Icon, Title } from '@/components/atoms';
import { FloatingActions } from '@/components/molecules';
import { Icons } from '@/components/atoms/Icon';

/**
 * Displays the intro Page before user starts a form.
 */
export const FormIntro = ({
  introPageItems,
  introCardDetails,
  title,
  backTo = '/care-profile',
  toAssistance,
  toForm,
  onNext,
}: FormIntroProps) => {
  return (
    <>
      <Container size='md'>
        <Wrapper>
          <ButtonWrapper>
            <Button
              ariaLabel='back'
              variant={'secondary'}
              to={backTo}
              iconLeft={'chevron-left'}
              size={'sm'}
            >
              {'Back'}
            </Button>
          </ButtonWrapper>

          <Title size='sm'>{title}</Title>

          <ContentWrapper>
            <ContentCard color='blue' isBordered={false} elevation={0}>
              <InfoGroup>
                {introPageItems.map(({ icon, text, iconColor }) => (
                  <InfoRow>
                    <IconCell>
                      <ColoredIcon
                        icon={icon as Icons}
                        iconColor={iconColor}
                        fontSize={30}
                      />
                    </IconCell>
                    <TextCell>{text}</TextCell>
                  </InfoRow>
                ))}
              </InfoGroup>
            </ContentCard>

            <InfoCard color={'yellow'} isBordered={false} elevation={0}>
              <InfoCardDetails>
                <InfoTitle>
                  <Icon icon={'info-circle'} fontSize={16} />
                  <InfoCardTitle>Did you know?</InfoCardTitle>
                </InfoTitle>
                <InfoCardContent>{introCardDetails}</InfoCardContent>
              </InfoCardDetails>
            </InfoCard>
          </ContentWrapper>
        </Wrapper>
      </Container>

      <FloatingActions
        actions={[
          {
            label: 'Get Assistance',
            variant: 'secondary',
            to: toAssistance,
          },
          {
            label: 'Start Form',
            to: toForm,
            onPress: onNext,
          },
        ]}
      />
    </>
  );
};

const ButtonWrapper = styled.div`
  width: fit-content;
  margin-bottom: ${(props) => props.theme.spacing[24]};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: ${(props) => props.theme.spacing[96]};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
`;

const InfoGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[24]};
`;

const InfoRow = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing[16]};
  align-items: center;
`;

const IconCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  min-width: 40px;
`;

const ContentCard = styled(Card)`
  padding: ${(props) => props.theme.spacing[32]};
`;

const TextCell = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
`;

const ColoredIcon = styled(Icon)<{ iconColor: 'blue' | 'success' | 'primary' }>`
  color: ${(props) =>
    props.iconColor === 'blue'
      ? props.theme.color.actionPlanCards.blue.primary
      : props.iconColor === 'success'
        ? props.theme.color.success[40]
        : props.theme.color.primary[70]};
`;

const InfoCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
  font: ${(props) => props.theme.typography.body.md.regular};
`;

const InfoCardDetails = styled.div`
  display: flex;
  flex-direction: column;
  color: ${(props) => props.theme.color.primary[80]};
  gap: ${(props) => props.theme.spacing[8]};
`;

const InfoCardTitle = styled.div`
  color: ${(props) => props.theme.color.primary[80]};
  font: ${(props) => props.theme.typography.heading['1xl']};
`;

const InfoTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
`;

const InfoCardContent = styled.div`
  color: ${(props) => props.theme.color.neutral[90]};
  font: ${(props) => props.theme.typography.body.md.regular};
`;

export interface IntroPageItems {
  icon: string;
  text: any;
  iconColor: 'blue' | 'success' | 'primary';
}

interface FormIntroProps {
  introPageItems: IntroPageItems[];
  introCardDetails: string;
  title: string;
  backTo?: string;
  toAssistance: string;
  toForm: string;
  onNext?: () => void;
}

FormIntro.displayName = 'FormIntro';

export default FormIntro;
